import { useState } from 'react';
import {
  type CreateReagentRecipeInput,
  type LabTest,
  type ReagentItemStock,
  type ReagentRecipeRow,
  type TestParameter,
  createReagentRecipeSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { RecipeQuantityCard, RecipeTargetCard } from './RecipeFields';
import {
  type RecipeLevel,
  clearOtherTarget,
  initialRecipeLevel,
  initialRecipeValues,
} from './recipe-form.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.reagentRecipes;

export function RecipeFormPage() {
  return (
    <ResourceFormShell<ReagentRecipeRow> path={PATH} noun="recipe">
      {({ id, record }) => <RecipeForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function RecipeForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: ReagentRecipeRow | undefined;
}) {
  const items = useAllRecords<ReagentItemStock>({ path: RESOURCE.reagentItems });
  const tests = useAllRecords<LabTest>({ path: RESOURCE.labTests });
  const parameters = useAllRecords<TestParameter>({ path: RESOURCE.testParameters });

  const [level, setLevel] = useState<RecipeLevel>(initialRecipeLevel(record));

  const { values, set, patch } = useFormValues<CreateReagentRecipeInput>(
    initialRecipeValues(record),
  );

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createReagentRecipeSchema,
    noun: 'Recipe',
  });

  function handleLevelChange(next: RecipeLevel) {
    setLevel(next);
    patch(clearOtherTarget(next));
  }

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <RecipeTargetCard
          values={values}
          onFieldChange={set}
          errors={form.errors}
          level={level}
          onLevelChange={handleLevelChange}
          tests={tests.items}
          parameters={parameters.items}
        />

        <RecipeQuantityCard
          values={values}
          onFieldChange={set}
          errors={form.errors}
          items={items.items}
        />

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Add recipe'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
